import { useEffect, useState } from 'react';
import { Stack, Card, Button, TextField, Typography, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getActivatedTwoFactorMethods, initiateTwoFactor, verifyTwoFactorActivation } from '../../services/post/twofa/twoFactorAuth';
import { TwoFactorMethod } from '../../services/post/twofa/interfaces';
import { useProfile } from '../../hooks/useProfile';

const TwoAuthContent = () => {
  const { t } = useTranslation('profile');
  const { profile } = useProfile();

  const [isSetupStarted, setIsSetupStarted] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasEmail2FA, setHasEmail2FA] = useState(false);

  const handleClick = async () => {
    setError(null);

    try {
      setIsLoading(true);

      if (!isSetupStarted) {
        await initiateTwoFactor(TwoFactorMethod.EMAIL);
        setIsSetupStarted(true);
      } else {
        if (!code.trim()) {
          setError(t('2FA.ERROR_EMPTY_CODE') || 'Please enter the code.');
          return;
        }

        await verifyTwoFactorActivation(code.trim(), TwoFactorMethod.EMAIL);

        setIsActivated(true);
        setCode('');
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        t('2FA.ERROR_GENERIC') ||
        'An error occurred while processing 2FA.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchActivatedMethods = async () => {
      try {
        const methods = await getActivatedTwoFactorMethods();
        if (methods.includes(TwoFactorMethod.EMAIL)) {
          setHasEmail2FA(true);
          setIsActivated(true);
        }
      } catch (e) {
        console.error('Failed to fetch activated 2FA methods', e);
      }
    };

    fetchActivatedMethods();
  }, []);

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      sx={{ width: '100%', minHeight: '100%' }}
    >
      <Card
        elevation={0}
        sx={{ p: 2, minWidth: 300, width: '500px', maxWidth: '100%' }}
      >
        <Stack mb={4}>
          <Typography variant="h6" gutterBottom>
            {t('2FA.TITLE')}
          </Typography>

          {!isSetupStarted && !isActivated && (
            <Typography variant="subtitle1">{t('2FA.SUBTITLE')}</Typography>
          )}
        </Stack>

        {error && (
          <Stack mb={2}>
            <Alert severity="error">{error}</Alert>
          </Stack>
        )}

        {!isActivated && (
          <Stack spacing={2}>
            {isSetupStarted && (
              <>
                <Typography variant="body1" fontWeight="bold">
                  {t('2FA.VERIFICATION_CODE_SEND_BY_EMAIL')}{' '}
                  <Typography
                    component="span"
                    variant="body1"
                    fontWeight="bold"
                    sx={{ color: '#3B89F7' }}
                  >
                    {profile?.email}
                  </Typography>
                </Typography>

                <TextField
                  label={t('2FA.LABEL_AUTHENTICATION_CODE')}
                  variant="outlined"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  fullWidth
                  disabled={isLoading}
                />
              </>
            )}

            <Button
              variant="contained"
              fullWidth
              onClick={handleClick}
              disabled={isLoading || hasEmail2FA || (isSetupStarted && !code.trim())}
            >
              {isSetupStarted
                ? t('2FA.BUTTON_VERIFY')
                : t('2FA.BUTTON_ACTIVATE')}
            </Button>
          </Stack>
        )}

        {isActivated && (
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ color: '#19b580', mt: 2 }}
          >
            {t('2FA.2FA_IS_NOW_ACTIVE')}
          </Typography>
        )}
      </Card>
    </Stack>
  );
};

export default TwoAuthContent;
